/**
 * MKS-332 — a money figure's name, with its definition one hover away.
 *
 * The ticket's requirement is that every money figure "carries its name and an
 * info tooltip with the one-sentence definition". Doing that with a shared
 * component rather than a per-surface `title=` means the definition is bound
 * to the metric KEY, not retyped per page — so the Project Report and the
 * Manager Overview cannot drift into explaining "Earned" differently.
 *
 * The tooltip is CSS-only (`group-hover` / `group-focus-within`), with the
 * same text also on the native `title` attribute. No portal, no popper, no
 * state: these labels sit in table headers that render hundreds of times, and
 * a hover tooltip is not worth a re-render budget. The `title` fallback is
 * what makes it work for a user who is tabbing rather than pointing, and in a
 * screenshot-based test.
 */
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { MONEY_GLOSSARY, type MoneyMetricKey } from '../../utils/moneyGlossary';

interface MoneyMetricLabelProps {
  metric: MoneyMetricKey;
  /** Overrides the glossary label — e.g. "Earned (last month)". The tooltip is unchanged. */
  label?: string;
  /** Tooltip alignment; use `right` for columns near the right edge of a table. */
  align?: 'left' | 'right';
  className?: string;
}

export function MoneyMetricLabel({
  metric,
  label,
  align = 'left',
  className = '',
}: MoneyMetricLabelProps) {
  const { label: glossaryLabel, definition } = MONEY_GLOSSARY[metric];
  const text = label ?? glossaryLabel;

  return (
    <span className={`group relative inline-flex items-center gap-1 ${className}`}>
      {text}
      <InformationCircleIcon
        className="h-3.5 w-3.5 shrink-0 cursor-help text-gray-400 group-hover:text-gray-600"
        aria-hidden="true"
        tabIndex={0}
        title={definition}
        data-testid={`money-metric-info-${metric}`}
      />
      <span
        role="tooltip"
        className={`pointer-events-none absolute top-full z-30 mt-1 hidden w-72 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal normal-case leading-snug text-white shadow-lg group-hover:block group-focus-within:block ${
          align === 'right' ? 'right-0' : 'left-0'
        }`}
      >
        {definition}
      </span>
    </span>
  );
}
